import { throwErrorWithId } from '@pypestream/utils';
import { useState } from 'react';

import { ActionFile } from '../components';
import { tableStore } from '../stores/table-store';
import { uploadFile, DataFileType, progressServerConfig } from '../utils';
import {
  useProStudioCtxSelector,
  sendProStudioEvent,
} from '../xstate/app.xstate';
import { ProStudioContext } from '../xstate/prostudio.xstate.types';

export const useUploadPythonFiles = (type: Omit<DataFileType, 'assetData'>) => {
  const [uploadingFiles, setUploadingFiles] = useState<ActionFile[]>([]);

  const { versionId, draft, projectId } = useProStudioCtxSelector((ctx) => ({
    versionId:
      ctx.customerId && ctx.botName && ctx.draftVersion
        ? `${ctx.customerId}.${ctx.botName}.${ctx.draftVersion}`
        : undefined,
    draft: ctx.draft,
    projectId: ctx.project?.id,
  }));

  const resetLoadingState = (fileName: string) => {
    setUploadingFiles((prev) => prev.filter((file) => file.name !== fileName));
  };

  const chunkArray = <T>(array: T[], size: number): T[][] =>
    Array.from({ length: Math.ceil(array.length / size) }, (_, i) =>
      array.slice(i * size, i * size + size)
    );

  const uploadFilesChunk = async (files: File[]) => {
    if (!versionId) {
      throwErrorWithId('Version ID is required');
    }

    if (!projectId) {
      throwErrorWithId('Project ID is required');
    }

    const newUploadingFiles = files.map((file) => ({
      name: `${file.name} (uploading: 0%)`,
      path: '',
      loading: true,
    }));

    setUploadingFiles((prev) => [...prev, ...newUploadingFiles]);

    await Promise.all(
      files.map((file) => {
        uploadFile({
          url: `${import.meta.env.FE_ADMIN_URL}/rest/bot-manager/${projectId}/${versionId}/${type === 'scriptFile' ? 'scripts' : 'config'}`,
          file: [file],
          type: type as DataFileType,
          onChange: ({ data: versionData }) => {
            sendProStudioEvent({
              type: 'updateDraftData',
              draft: (versionData as ProStudioContext['draft']) || draft,
              action: 'upload',
            });
            resetLoadingState(`${file.name} (uploading: 100%)`);
          },
          error: (error) => {
            console.error(error);
            resetLoadingState(`${file.name} (uploading: 100%)`);
            sendProStudioEvent({
              type: 'showError',
              error,
            });
          },
          progress: (current, loaded, total) => {
            progressServerConfig(current, loaded, total, (p) => {
              setUploadingFiles((prev) =>
                prev.map((uploadingFile) =>
                  uploadingFile.name.startsWith(file.name)
                    ? {
                        ...uploadingFile,
                        name: `${file.name} (uploading: ${p}%)`,
                      }
                    : uploadingFile
                )
              );
            });
          },
        });
      })
    );
  };

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = e.target;
    if (!files) return;

    const batchSize = 10;
    const batches = chunkArray(Array.from(files), batchSize);

    for (const batch of batches) {
      await uploadFilesChunk(batch);
    }

    await tableStore.validateAndSync();
  };

  return {
    uploadingFiles,
    handleUpload,
  };
};
